import React, { useState, useEffect, useMemo } from 'react';
import ProprietarioList from '../components/ProprietarioList';
import ProprietarioForm from '../components/ProprietarioForm';
import api from '../services/api';
import { Container, Typography, Box, Button, IconButton, Collapse, TextField, Autocomplete, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import { debounce } from 'lodash';
import { actionButtonSx, filterCardSx, pageTitleSx } from '../styles/uiTokens';

function ProprietariosPage() {
  const [refreshList, setRefreshList] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [nomeFilter, setNomeFilter] = useState('');
  const [selectedImovelIdFilter, setSelectedImovelIdFilter] = useState('');

  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [selectedImovelFilter, setSelectedImovelFilter] = useState(null);

  const debouncedFetchImoveis = useMemo(() => debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then((response) => setImovelOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar imóveis para filtro:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchImoveis.cancel();
    };
  }, [debouncedFetchImoveis]);

  useEffect(() => {
    setRefreshList((prev) => !prev);
  }, [nomeFilter, selectedImovelIdFilter]);

  return (
    <Container sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={pageTitleSx}>Gerenciar Proprietários</Typography>
        <Box>
          {!showForm && (
            <>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingItem(null); setShowForm(true); }} sx={{ ...actionButtonSx, mr: 1 }}>
                Adicionar Proprietário
              </Button>
              <IconButton onClick={() => setShowFilters((prev) => !prev)} sx={{ color: 'primary.main' }}>
                <FilterListIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {!showForm && (
        <Collapse in={showFilters}>
          <Box sx={filterCardSx}>
            <Typography variant="h6" gutterBottom>Filtros</Typography>
            <TextField
              label="Nome do Proprietário"
              fullWidth
              margin="normal"
              value={nomeFilter}
              onChange={(e) => setNomeFilter(e.target.value)}
            />
            <Autocomplete
              options={imovelOptions}
              getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedImovelFilter}
              onChange={(event, newValue) => {
                setSelectedImovelFilter(newValue);
                setSelectedImovelIdFilter(newValue ? newValue.id : '');
              }}
              onInputChange={(event, value, reason) => {
                if (reason === 'input') {
                  if (value) debouncedFetchImoveis(value);
                  else setImovelOptions([]);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Residência (Imóvel)"
                  margin="normal"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Collapse>
      )}

      {showForm && (
        <ProprietarioForm
          onProprietarioAdded={() => { setRefreshList((prev) => !prev); setEditingItem(null); setShowForm(false); }}
          initialData={editingItem}
          onCancelEdit={() => { setEditingItem(null); setShowForm(false); }}
        />
      )}

      {!showForm && (
        <ProprietarioList
          refresh={refreshList}
          onEdit={(row) => { setEditingItem(row); setShowForm(true); }}
          onDeleted={() => setRefreshList((prev) => !prev)}
          nomeFilter={nomeFilter}
          imovelIdFilter={selectedImovelIdFilter}
        />
      )}
    </Container>
  );
}

export default ProprietariosPage;
